import { Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import { IPerson, IUser } from ".";
import { CirculationFacade } from "../services/circulation.facade";
import { UserId } from "./abstractUserService";
import { DateUtility } from "./date";
import { Entity, EntityId } from "./entity";
import { ICirculationTemplateStep, StepId } from "./step";

export type prepareId = number | string; // really? number? - no :D
export type CirculationId = EntityId;

export type CirculationStatus =
  | "InCreation"
  | "InProgress"
  | "Canceled"
  | "Finished"
  | "Errored"; // see table dbo.CirculationStatus

export type OutcomeStatus = "Approved" | "Rejected";

export interface ICirculationPayload {
  demand: string;
  data?: IMetaData[];
  additionalMailRecipients?: IPerson[]; // What is the difference between IUser and IPerson?!
}

export interface IMetaData {
  key: string;
  displayValues: string[];
  value: unknown;
  sort?: number;
  valueType:
    | "boolean"
    | "string"
    | "number"
    | "link"
    | "choice"
    | "date"
    | "PropertyFileArea"
    | "PropertyFileCompany"
    | "PropertyFileConcept"
    | "PropertyFileContract"
    | "PropertyFileObject"
    | "PropertyFileTenant";
  settings:
    | IBooleanMetaDataSettings
    | IStringMetaDataSettings
    | INumberMetaDataSettings
    | IChoiceMetaDataSettings
    | IPropertyFileFilterMetaDataSettings;
}

export interface IBaseMetaDataSettings {
  isReadonly: boolean;
  isVisibleInUi: boolean;
  isRequired: boolean;
}

export type IBooleanMetaDataSettings = IBaseMetaDataSettings;

export type IStringMetaDataSettings = IBaseMetaDataSettings;

export interface INumberMetaDataSettings extends IBaseMetaDataSettings {
  min: number;
  max: number;
  decimals: number;
}

export interface IChoiceMetaDataSettings extends IBaseMetaDataSettings {
  choices: string[];
  default: string;
  isMultiSelect: boolean;
}

export type IPropertyFileFilterMetaDataSettings = IBaseMetaDataSettings;

export interface ICirculationInit {
  id: CirculationId;
  owner?: IUser[]; // What is the difference between IUser and IPerson?!
  // startDate?: Date;
  payload: ICirculationPayload;
  status: CirculationStatus;
  outcome?: OutcomeStatus;
  title: string;
  dueDate?: Date; //Only get no set
}

export interface IMyTask {
  assignedToId: number;
  circulationId: number;
  circulationStatus: string;
  circulationTitle: string;
  dueDate?: Date;
  stepComment: string;
  stepId: number;
  stepStatus: string;
  stepTaskResult: string;
  stepTitle: string;
}

export interface ICirculationTemplate {
  circulationTitle: string;
  duration: number;
  id: CirculationId;
  payload: ICirculationPayload;
  title: string;
  steps: ICirculationTemplateStep[];
  permissions: ICirculationTemplatePermissions;
}

export interface ICirculationTemplatePermissions {
  ownerId: UserId;
}

export interface IGridOptions {
  data: unknown;
}

export interface IStartMultipleCirculationsResult {
  success: number;
  fails: number;
  messages: string[];
}

export class Circulation extends Entity<ICirculationInit> {
  constructor(props: ICirculationInit, id?: EntityId) {
    super(props, 0, id);
  }

  public canCancel(user: IUser, isAdmin: boolean): Observable<boolean> {
    return this.canEdit(user, isAdmin);
  }

  public canEdit(user: IUser, isAdmin: boolean): Observable<boolean> {
    if (
      this.props.status !== "InCreation" &&
      this.props.status !== "InProgress"
    ) {
      return of(false);
    }
    return of(this.userCanManage(user, isAdmin));
  }

  public canCopy(user: IUser, isAdmin: boolean): Observable<boolean> {
    if (
      !(
        this.props.status === "Canceled" ||
        (this.props.status === "Finished" && this.props.outcome == "Rejected")
      )
    ) {
      return of(false);
    }
    return of(this.userCanManage(user, isAdmin));
  }

  public canOverride(user: IUser, isAdmin: boolean): Observable<boolean> {
    if (this.props.status !== "InProgress") {
      return of(false);
    }

    return of(this.userCanManage(user, isAdmin));
  }

  public canStart(user: IUser, isAdmin: boolean): Observable<boolean> {
    if (this.props.status !== "InCreation") {
      return of(false);
    }
    return of(this.userCanManage(user, isAdmin));
  }

  private userCanManage(user: IUser, isAdmin: boolean) {
    return (
      isAdmin || this.props.owner.filter((u) => u.id == user.id)?.length > 0
    );
  }

  /**
   * Return the valid range of dueDates, including borders (i.e. from and to are valid).
   * @param circulationService an implementation of AbstractCirculationsService
   * @param stepId the stepId to calculate the date-range for, or `null` for a new step.
   */
  public getValidDueDateRangeForStep(
    circulationFacade: CirculationFacade,
    stepId: StepId | null
  ): Observable<{ from: Date; to: Date }> {
    return circulationFacade.getSteps(this.id).pipe(
      map((steps) => {
        const minFrom = DateUtility.getTodayMin(); // not before today

        let from: Date = null;
        let to: Date = null;

        if (!stepId) {
          // we want the duedate and it could be null for step types
          const prevStep = steps
            .filter((s) => s.props.dueDate)
            .sort((a, b) => b.props.stepNo - a.props.stepNo)[0];
          if (prevStep) {
            from = prevStep.props.dueDate;
          }
        } else if (stepId) {
          const step = steps.find((s) => s.id == stepId); //id could be number or string: use "==" and not "==="
          if (!step) {
            throw new Error("no step with id:" + stepId);
          }

          const prevStep = steps
            .filter((s) => s.props.dueDate)
            .filter((s) => s.props.stepNo < step.props.stepNo)
            .sort((a, b) => b.props.stepNo - a.props.stepNo)[0];
          if (prevStep) {
            from = prevStep.props.dueDate;
          }

          const nextStep = steps
            .filter((s) => s.props.dueDate)
            .filter((s) => s.props.stepNo > step.props.stepNo)
            .sort((a, b) => a.props.stepNo - b.props.stepNo)[0];
          if (nextStep) {
            to = nextStep.props.dueDate;
          }
        }

        return {
          from: from || minFrom,
          to: to
        };
      })
    );
  }

  /**
   * true, if Metadata has errors.
   * For full information every step needs to be checked for errors, too!
   */
  public static hasMetadataErrors(props: ICirculationInit): boolean {
    const visibleMetadata = (props.payload.data ?? []).filter(
      (x) => x.settings.isVisibleInUi
    );

    return visibleMetadata.some((m) => {
      const isRequired = m.settings.isRequired;
      const hasValue = !!m.value;

      return isRequired && !hasValue;
    });
  }
}
